import React, { useEffect, useCallback, useMemo } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { generatePath, useHistory, useLocation, useParams } from 'react-router-dom';
import {
  FormCard,
  YupNumeric,
  YupString,
  YupBoolean,
  promiseModalDialog,
  isNumeric,
  toastSuccess,
  YupStringReq,
  YupNumericReq,
  toastWarning,
  ButtonAction,
  YupObject,
} from 'motion-components';
import * as Yup from 'yup';
import * as Lo from 'lodash';
import {
  gqlGetByIdQuery,
  gqlAdd,
  gqlUpdate,
  gqlDelete,
  FormInput,
  gqlClearPassword,
  gqlAddRecursiveContribution,
} from '../graphql';
import '../../../../styles/tab-styles.css';
import { FormFields } from './form-fields';
import { UserParamsSection } from './user-params';
import { useRootStore } from 'store';
import { useHandleError } from 'shared-pages/services';
import { useTranslation } from 'react-i18next';
import { RowFieldTooltips, UserTypeValues } from 'shared-pages';
import setPasswordModal from 'app/set-password-modal';
import { getRouteByName } from 'app/routes';
import { UserRoles } from 'common/constants';
import promiseModalSelectYear from 'shared-pages/components/select-dlg/modal-select-year';

import { isRebattery, isRebatteryLiion, requiredString } from 'shared-pages/common/constants';

// Customize FormShape based on Customer/Company
function useCustomFormShape(base: any) {
  const { t } = useTranslation();
  const reqStr = t(requiredString);

  let formShape = Yup.object(base);

  if (isRebatteryLiion) {
    formShape = formShape.shape({
      referenceId: Yup.number().nullable().notRequired(),
    });
  }

  return formShape;
}

export const UserFormPage = () => {
  const { handleGraphqlError } = useHandleError('user');
  const { appStore, authStore } = useRootStore();
  const history = useHistory();
  const location: any = useLocation();
  const { id } = useParams<{ id: string }>();
  const idNum = isNumeric(id) ? parseInt(id) : -1;
  const isNew = idNum < 0;
  const { t } = useTranslation();

  const formShape = {
    id: YupNumeric,
    userName: YupStringReq,
    firstName: YupStringReq,
    lastName: YupStringReq,
    email: YupStringReq,
    tel: YupString,
    mobile1: YupString,
    position: YupString,
    notes: YupString,
    usertypeId: YupNumericReq,
    referenceId: YupNumeric.when('usertypeId', {
      is: (usertypeId) => {
        return usertypeId !== UserTypeValues.PlatformOwner;
      },
      then: YupNumeric.required(t(requiredString)),
      otherwise: YupNumeric.nullable().notRequired(),
    }),
    referenceId2: YupNumeric,
    signatureFileId: YupNumeric,
    isActive: YupBoolean,
    userview: YupObject,
  };

  // const validationSchema = Yup.object().shape(formShape);
  const validationSchema = useCustomFormShape(formShape);
  const pickProperties = (values) => Lo.pick(values, Lo.keys(formShape));
  type FormValuesType = Yup.InferType<typeof validationSchema>;

  const newRecord = useMemo<FormValuesType>(
    () =>
      ({
        id: -1,
        userName: null,
        isActive: true,
      } as FormValuesType),
    [],
  );

  function calcCanDelete() {
    if ((isRebatteryLiion || isRebattery) && appStore.isContributor) {
      return idNum === appStore.userLogin.userId ? false : true;
    }
    return appStore.isPlatformOwner ? true : false;
  }
  const canDelete = calcCanDelete();

  const [addRecord] = useMutation(gqlAdd);
  const [updateRecord] = useMutation(gqlUpdate);
  const [deleteRecord] = useMutation(gqlDelete);
  const [clearPassword] = useMutation(gqlClearPassword);
  const [addCnt] = useMutation(gqlAddRecursiveContribution);

  const [get, { data: rawData, error, refetch }] = useLazyQuery(gqlGetByIdQuery);
  // const data = rawData ? rawData[Object.keys(rawData)[0]] : undefined;
  const data = isNew ? newRecord : rawData ? rawData[Object.keys(rawData)[0]] : undefined;

  /* fetch data */
  useEffect(() => {
    get({ variables: { id: idNum } });
  }, [get, id, idNum]);

  // Handler να πάει στην πίσω σελίδα
  const handleReturn = useCallback(() => {
    history.push(generatePath(getRouteByName('Users')), { ...location.state });
  }, [history, location.state]);

  // Handler να πάει στην δήλωση
  const navigateToContribution = async (contributionId: number) => {
    history.push({
      pathname: generatePath(getRouteByName('Contribution'), { id: contributionId }),
      state: {
        pathname: location.pathname,
        ...location.state,
      },
    });
  };

  /**
   * * ΑΠΟΘΗΚΕΥΣΗ - UPDATE/CREATE
   */
  const handleSubmit = (values: FormValuesType, actions) => {
    actions.setSubmitting(false);
    const usertypeId = (values as any).usertypeId;
    //console.log('$$$', usertypeId, JSON.stringify(!(values as any).referenceId));
    if (usertypeId !== UserTypeValues.PlatformOwner && !(values as any).referenceId) {
      let doReturn = true;
      if (usertypeId === UserTypeValues.Collector) toastWarning(t('Πρέπει να δηλώσετε Συλλέκτη'));
      else if (usertypeId === UserTypeValues.CollectorDriver)
        toastWarning(t('Πρέπει να δηλώσετε Οδηγό'));
      else if (usertypeId === UserTypeValues.WasteReceptionPoint)
        toastWarning(t('Πρέπει να δηλώσετε Ανακυκλωτή/Αποθήκη'));
      else if (usertypeId === UserTypeValues.CollectionPoint) {
        if (isRebatteryLiion) doReturn = false;
        else toastWarning(t('Πρέπει να δηλώσετε Σημείο Συλλογής'));
      }
      console.log('doReturn', doReturn);
      if (doReturn === true) return;
    }
    // Αν είναι νέα εγγραφή τοτε γράφω όλα τα πεδία
    if (isNew) {
      console.log('[User] New:', values);
      const mutValues = Lo.pick(values, Lo.keys(new FormInput()));
      addRecord({ variables: { data: { ...mutValues } } })
        .then((d) => {
          toastSuccess(`${t('Επιτυχής Προσθήκη Εγγραφής')} [${d.data.addUser.id}]`);
          handleReturn();
        })
        .catch((error) => {
          handleGraphqlError('mutation-add', error);
        });
    } else {
      const mutValues = Lo.pick(values, Lo.keys(new FormInput()));
      // Αν είναι update εγγραφής τοτε γράφω μονο τα αλλαγμένα πεδία
      console.log('[Applog] Update:', mutValues);
      updateRecord({ variables: { data: { ...mutValues, id: idNum } } })
        .then((d) => {
          toastSuccess(`Επιτυχής Ενημέρωση Εγγραφής [${d.data[Object.keys(d.data)[0]].id}]`);
          refetch();
        })
        .catch((error) => {
          handleGraphqlError('mutation-update', error);
        });
    }
  };

  /**
   * *  DELETE
   */
  const handleDelete = useCallback(async () => {
    const res = await promiseModalDialog({
      title: 'Διαγραφή Εγγραφής',
      text: 'Είστε σίγουροι ότι θέλετε να διαγράψετε την Εγγραφή;',
    });
    if (res) {
      deleteRecord({ variables: { id: idNum } })
        .then(() => history.goBack())
        .catch((error) => {
          handleGraphqlError('mutation-delete', error);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteRecord, idNum]);

  /**
   * * SET NEW PASSWORD Handler
   */
  const handleClearPassword = useCallback(
    async (user) => {
      const res = await promiseModalDialog({
        title: 'Μηδενισμός Κωδικού Πρόσβασης',
        text: `Ο Κωδικός Πρόσβασης του χρήστη ${user} θα μηδενιστεί, και ο χρήστης θα πρέπει να καθορίσει νέο κωδικό πρόσβασης την επόμενη φορά που θα συνδεθεί.`,
      });
      if (res) {
        const variables = { userName: user };
        clearPassword({ variables })
          .then(() => {
            toastSuccess(`Επιτυχής Μηδενισμός Κωδικού Πρόσβασης`);
          })
          .catch((error) => {
            handleGraphqlError('clear-password', error, 'Ο κωδικός πρόσβασης δεν μηδενίστηκε');
          });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [clearPassword, handleGraphqlError],
  );

  /**
   * * Add Recursive Contribution
   */
  const addRecursiveCnt = async () => {
    const res = await promiseModalSelectYear({
      title: t(`Δημιουργία Αναδρομικής Δήλωσης`),
      text: ``,
    });
    if (res) {
      console.log(res);
      addCnt({
        variables: { contributorId: data.referenceId, year: res.year, periodId: 17 },
      })
        .then((d) => {
          const contributionId = (d as any).data.addRecursiveContribution;
          toastSuccess(`${t('Δημιουργήθηκε Νέα Δήλωση με Αριθμό:')} ${contributionId}`);
          navigateToContribution(contributionId);
        })
        .catch((error) => {
          handleGraphqlError('mutation-addRecursiveContribution', error);
        });
    }
  };
  /*  -----------------------------------------------------
      Render
      -----------------------------------------------------
  */
  if (error) {
    handleGraphqlError('query-get', error);
    return <h3 className="text-center">{error.message}</h3>;
  } else {
    return (
      <>
        <FormCard
          title={`${t('Χρήστης')}: ${data?.userName ?? 'Νέος Χρήστης'}`}
          onSubmit={handleSubmit}
          onDelete={handleDelete}
          onReturn={handleReturn}
          permitSubmit={false}
          permitDelete={canDelete}
          initialValues={pickProperties(data)}
          validationSchema={validationSchema}
          enableReinitialize
          idx={idNum}
          actions={
            <div className="d-flex align-items-center">
              {((appStore.isContributor && data?.id === appStore.userLogin.userId) ||
                (data?.usertypeId === UserTypeValues.Contributor && appStore.isPlatformOwner)) && (
                <ButtonAction
                  variant="primary"
                  style={{ marginRight: '6px' }}
                  text={t('Δημιουργία Αναδρομικής Δήλωσης')}
                  onClick={addRecursiveCnt}
                ></ButtonAction>
              )}
              {(appStore.userLogin.userId === data?.id ||
                (!isNew &&
                  appStore.userLogin.userId !== data?.id &&
                  authStore.isPlatforOwner &&
                  data?.userTypeId !== UserTypeValues.CollectorDriver)) && (
                <ButtonAction
                  className={appStore.userLogin.userId === data?.id ? 'ml-2' : 'ms-2'}
                  text="Αλλαγή Κωδικού Πρόσβασης"
                  variant="warning"
                  onClick={setPasswordModal}
                ></ButtonAction>
              )}
              {!isNew &&
                appStore.userLogin.userId !== data?.id &&
                authStore.isPlatforOwner &&
                data?.userTypeId !== UserTypeValues.CollectorDriver && (
                  <ButtonAction
                    className="ms-2"
                    text="Μηδενισμός Κωδικού Πρόσβασης"
                    variant="warning"
                    onClick={() => handleClearPassword(data?.userName)}
                  ></ButtonAction>
                )}
            </div>
          }
        >
          <FormFields isNew={isNew} updateRecord={updateRecord} refetch={refetch} data={data} />
          {appStore.isPlatformOwner && <RowFieldTooltips data={data} showLegacy={false} />}
        </FormCard>
        {!isNew &&
          data?.userName &&
          (appStore.userLogin?.userId === data?.id || appStore.isPlatformOwner) && (
            <UserParamsSection userName={data.userName} userTypeId={data.usertypeId} />
          )}
      </>
    );
  }
};
